import { z } from 'zod';
import { PlanFeaturesSchema } from './plan-features-schema.js';
import { CollectionMethodSchema, SubscriptionStatusSchema } from './plan-schemas.js';
// saas-monetization-plans v2 — WU-I.1: org_admin-facing subscription
// visibility contracts (design §5/§8, spec "Org Admin Visibility"). Carries
// `features` (provider-neutral, powers conditional UI) and current-period
// `usage` (vehicle count vs. quota) so the frontend never has to guess at
// gating or overage state. NO `stripe*` field names anywhere in this file.
//
// `SubscriptionStatusSchema` is defined in `plan-schemas.ts` (shared by both
// the plan-assignment response and this file) — re-exported from there via
// the package index, NOT re-exported here, to avoid a duplicate `export *`
// symbol collision.
export const SubscriptionUsageSchema = z.object({
    periodStart: z.string(),
    periodEnd: z.string(),
    vehicleCount: z.number().int().min(0),
    includedVehicles: z.number().int().min(0),
    overageUnits: z.number().int().min(0),
    overageAmountCents: z.number().int().min(0),
});
export const SubscriptionResponseSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    planId: z.string().uuid(),
    planName: z.string(),
    status: SubscriptionStatusSchema,
    currentPeriodStart: z.string().nullable(),
    currentPeriodEnd: z.string().nullable(),
    // Dunning deadline: while `past_due`, the instant after which the
    // subscription is suspended (org_admin banner shows it). Optional so a
    // client/server pair mid-deploy still parses.
    graceUntil: z.string().nullable().optional(),
    cancelAtPeriodEnd: z.boolean(),
    canceledAt: z.string().nullable(),
    cancellationReason: z.string().nullable(),
    // Commission is per-org (Organization.platformFeeBasisPoints), not
    // per-subscription — surfaced here read-only so org_admin sees "not
    // applicable" when the assigned plan has commission disabled.
    commissionEnabled: z.boolean(),
    commissionBasisPoints: z.number().int().min(0).max(10000).nullable(),
    features: PlanFeaturesSchema,
    // null when the subscription has never had an active period yet
    // (still 'incomplete', card setup not completed).
    usage: SubscriptionUsageSchema.nullable(),
    // manual-subscription-billing (design §1) — set at assignment time,
    // immutable afterward. REQUIRED (not optional) — every subscription has
    // one, defaulting to 'automatic' at the domain layer.
    collectionMethod: CollectionMethodSchema,
});
// ── Cancel ───────────────────────────────────────────────────────────────
//
// Both fields are OPTIONAL and NEVER a precondition for cancellation (legal
// requirement, spec "Legal cancellation with retention friction") — the
// backend accepts them purely for reporting.
export const CancelSubscriptionRequestSchema = z.object({
    reason: z.string().trim().max(2000).optional(),
    retentionOfferAccepted: z.boolean().optional(),
});
export const CancelSubscriptionResponseSchema = z.object({
    subscriptionId: z.string().uuid(),
    status: SubscriptionStatusSchema,
    cancelAtPeriodEnd: z.boolean(),
    cancellationReason: z.string().nullable(),
    currentPeriodEnd: z.string().nullable(),
});
// ── Invoice history ──────────────────────────────────────────────────────
export const SubscriptionInvoiceStatusSchema = z.enum(['open', 'paid', 'uncollectible', 'void']);
// manual-subscription-billing (design §2.1, spec REQ-6.1/6.2) — itemized
// breakdown of a SubscriptionInvoice. Present ONLY for manual invoices —
// Stripe owns the automatic path's own line-item breakdown, so automatic
// invoices always report an empty array here (S5.5/S6.4 parity).
export const SubscriptionInvoiceLineSchema = z.object({
    kind: z.enum(['base', 'overage']),
    description: z.string(),
    quantity: z.number().int().positive(),
    unitAmountCents: z.number().int().min(0),
    amountCents: z.number().int().min(0),
});
// manual-subscription-billing (design §1.1/§1.2/§1.4) — `providerInvoiceRef`
// became nullable (manual invoices carry no Stripe ref); `invoiceNumber` is
// the new internal per-org identity (null for automatic); `dueAt` is the
// net-terms due date (manual only, null for automatic); `lines` is the
// itemized breakdown (empty for automatic).
export const SubscriptionInvoiceResponseSchema = z.object({
    // manual-subscription-billing (WU8) — optional: the org_admin invoice-
    // history view never needed it; the super_admin manual-payment-recording
    // UI does (to address a specific invoice for POST .../record-payment).
    id: z.string().uuid().optional(),
    providerInvoiceRef: z.string().nullable(),
    invoiceNumber: z.number().int().nullable(),
    amountCents: z.number().int(),
    currency: z.string(),
    status: SubscriptionInvoiceStatusSchema,
    periodStart: z.string().nullable(),
    periodEnd: z.string().nullable(),
    dueAt: z.string().nullable(),
    paidAt: z.string().nullable(),
    createdAt: z.string(),
    lines: z.array(SubscriptionInvoiceLineSchema),
});
export const ListSubscriptionInvoicesResponseSchema = z.object({
    invoices: z.array(SubscriptionInvoiceResponseSchema),
});
// ── Overage charge history (invoice line items, broken out) ────────────────
export const SubscriptionOverageChargeStatusSchema = z.enum([
    'pending',
    'charged',
    'skipped',
    'failed',
]);
export const SubscriptionOverageChargeResponseSchema = z.object({
    id: z.string().uuid(),
    periodStart: z.string(),
    periodEnd: z.string(),
    vehicleCount: z.number().int(),
    includedVehicles: z.number().int(),
    overageUnits: z.number().int(),
    amountCents: z.number().int(),
    currency: z.string(),
    status: SubscriptionOverageChargeStatusSchema,
    createdAt: z.string(),
});
export const ListSubscriptionOverageChargesResponseSchema = z.object({
    overageCharges: z.array(SubscriptionOverageChargeResponseSchema),
});
//# sourceMappingURL=subscription-schemas.js.map